// ControlRisksTab — lists risks linked to the current control.
// Data comes from GET /controls/{control_id}/risks.
// Each row links to the risk detail page.

import { useParams, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { controlsApi, type LinkedRiskSummary } from '@/lib/api/controls'
import { lifecycleStateLabels, lifecycleStateColors } from '@/lib/api/risks'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { TableSkeleton } from '@/components/shared/LoadingSkeleton'
import { Badge } from '@/components/ui/badge'
import { AlertTriangle, FolderOpen, User, Calendar, ExternalLink } from 'lucide-react'
import { cn } from '@/lib/utils'

export function ControlRisksTab() {
  const { slug } = useTenant()
  const { controlId } = useParams({ strict: false }) as { controlId: string }
  const navigate = useNavigate()
  const api = useApi()
  const ctrlApi = controlsApi(api)

  const {
    data: risks,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: queryKeys.controls.risks(slug, controlId),
    queryFn: () => ctrlApi.risks(controlId),
  })

  if (isLoading) return <TableSkeleton rows={4} />

  if (error) {
    return (
      <div className="p-6">
        <ErrorState
          message="Failed to load linked risks"
          technicalDetail={error.message}
          onRetry={() => refetch()}
        />
      </div>
    )
  }

  if (!risks || risks.length === 0) {
    return (
      <div className="flex h-48 items-center justify-center text-muted-foreground">
        <div className="space-y-2 text-center">
          <AlertTriangle className="mx-auto h-10 w-10 text-muted-foreground/40" />
          <p className="text-sm">No risks are linked to this control.</p>
          <p className="text-xs text-muted-foreground/60">
            Risks can be linked from the Risks section.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mb-3 text-sm text-muted-foreground">
        {risks.length} linked risk{risks.length !== 1 ? 's' : ''}
      </div>
      <div className="divide-y rounded-lg border">
        {risks.map((risk) => (
          <RiskRow
            key={risk.id}
            risk={risk}
            onClick={() => navigate({ to: `/t/${slug}/risks/${risk.id}` })}
          />
        ))}
      </div>
    </div>
  )
}

function RiskRow({
  risk,
  onClick,
}: {
  risk: LinkedRiskSummary
  onClick: () => void
}) {
  return (
    <div
      className="group flex cursor-pointer items-center gap-4 px-4 py-3 transition-colors hover:bg-accent/30"
      onClick={onClick}
    >
      {/* Name */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{risk.name}</span>
          <ExternalLink className="h-3 w-3 flex-shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100" />
        </div>
      </div>

      {/* Register */}
      <div className="hidden items-center gap-1 text-xs text-muted-foreground md:flex">
        <FolderOpen className="h-3 w-3" />
        <span className="max-w-[120px] truncate">{risk.register_name}</span>
      </div>

      {/* Owner */}
      {risk.owner_display_name && (
        <div className="hidden items-center gap-1 text-xs text-muted-foreground lg:flex">
          <User className="h-3 w-3" />
          <span className="max-w-[100px] truncate">{risk.owner_display_name}</span>
        </div>
      )}

      {/* Linked date */}
      <div className="hidden items-center gap-1 text-xs text-muted-foreground xl:flex">
        <Calendar className="h-3 w-3" />
        <span>{new Date(risk.linked_at).toLocaleDateString()}</span>
      </div>

      {/* Status badge */}
      <Badge
        variant="outline"
        className={cn(
          'flex-shrink-0 px-1.5 py-0 text-xs',
          lifecycleStateColors[risk.lifecycle_state],
        )}
      >
        {lifecycleStateLabels[risk.lifecycle_state] ?? risk.lifecycle_state}
      </Badge>
    </div>
  )
}
